<template>
  <BaseWidget
    title="Public group conversations"
    :show-info-icon="true"
    info-tooltip="Total number of public group conversations in your community."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/10509690-creating-and-managing-group-messages-in-my-inbox', text: 'Learn about Group Messaging' }"
    :show-settings="false"
    :disable-date-range="true"
    :edit-mode="editMode"
    :widget-id="widgetId"
    :widget-type="widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    @remove-widget="$emit('remove-widget', widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
        :show-table="!focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <MetricValue
        :value="totalCount"
        subtitle="Groups"
        :has-data-table="topGroups.length > 0 && !focused"
      >
        <template
          v-if="conversations.length > 0"
          #subtitle-suffix
        >
          <span
            class="focused-view-all-link"
            @click="handleViewAllClick"
          >View all</span>
        </template>
      </MetricValue>
      <div
        v-if="!focused && topGroups.length > 0"
        class="groups-list"
      >
        <div
          v-for="(group, index) in topGroups"
          :key="group.conversation_id || index"
          class="group-item"
          @click="handleGroupClickGuarded(group)"
        >
          <div class="group-row">
            <div class="group-image">
              <img
                v-if="group.avatar"
                :src="group.avatar"
                :alt="group.name"
              >
              <div
                v-else
                class="image-placeholder"
              >
                {{ getInitials(group.name) }}
              </div>
            </div>
            <div class="group-info">
              <span class="group-name">{{ group.name || 'Unnamed Group' }}</span>
              <span class="group-messages">{{ formatMessageCount(group.messageCount) }}</span>
            </div>
            <span
              :class="group.joined ? 'joined-chip' : 'not-joined-chip'"
            >{{ group.joined ? 'Joined' : 'Not joined' }}</span>
          </div>
        </div>
      </div>
      <div
        v-else-if="!loading && topGroups.length === 0"
        class="no-groups"
      >
        No group conversations
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getDemoPublicGroupConversationsData } from '../services/demoData.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { pushConversationPanel, pushAdminSidePanel } from '../composables/useOptixPanel.js'
import { useDemoMode } from '../composables/useDemoMode.js'
import { getOrganizationIdFromURL } from '../utils/tokenHandler.js'

const props = defineProps({
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  },
  editMode: {
    type: Boolean,
    default: false
  },
  widgetId: {
    type: String,
    default: null
  },
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  }
})

defineEmits(['remove-widget'])

const organizationId = computed(() => getOrganizationIdFromURL())

const publicGroupConversationsQuery = `
  query PublicGroupConversations(
    $organizationId: ID
    $includeJoinable: [ConversationType!]
    $conversationType: [ConversationType!]
  ) {
    conversations(
      organization_id: $organizationId
      include_joinable: $includeJoinable
      conversation_type: $conversationType
    ) {
      total
      data {
        conversation_id
        name
        message_count
        image {
          url
        }
        conversation_type
        users
      }
    }
  }
`

const { data, loading, error, cleanup } = useGraphQLData({
  dataKey: 'public_group_conversations',
  query: publicGroupConversationsQuery,
  operationName: 'PublicGroupConversations',
  variables: computed(() => ({
    organizationId: organizationId.value || undefined,
    includeJoinable: ['PUBLIC_GROUP'],
    conversationType: ['PUBLIC_GROUP']
  })),
  priority: props.priority ?? 1,
  getDemoData: () => getDemoPublicGroupConversationsData()
})

const demoMode = useDemoMode()

const { data: meData } = useGraphQLData({
  dataKey: 'public_groups_current_user',
  query: `query GetCurrentUser { me { user { user_id } } }`,
  operationName: 'GetCurrentUser',
  variables: {},
  priority: 10,
  enableBatching: true,
  autoFetch: true
})

const currentUserId = computed(() => meData.value?.me?.user?.user_id || '')

const conversations = computed(() => {
  const items = data.value?.conversations?.data ?? []
  return items.map((item) => {
    const users = Array.isArray(item.users) ? item.users : []
    const joined = demoMode?.value === true
      ? item.joined ?? false
      : currentUserId.value && users.some(u => String(u) === String(currentUserId.value))
    return {
      conversation_id: item.conversation_id,
      name: item.name || 'Unnamed Group',
      avatar: getAvatarUrl(item.image?.url, item.name || item.conversation_id),
      messageCount: item.message_count || 0,
      joined
    }
  })
})

const totalCount = computed(() => {
  const total = data.value?.conversations?.total
  if (total != null) return total.toString()
  return conversations.value.length.toString()
})

const topGroups = computed(() => {
  return [...conversations.value]
    .sort((a, b) => b.messageCount - a.messageCount)
    .slice(0, 3)
})

const formatMessageCount = (count) => {
  if (count === 1) return '1 message'
  return `${count.toLocaleString()} messages`
}

const { guard } = useTrialModeGuard()
const handleGroupClickGuarded = guard((group) => {
  if (!group?.conversation_id) return
  pushConversationPanel(group.conversation_id)
})

const handleViewAllClick = guard(() => {
  pushAdminSidePanel('view-all-groups')
})

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 12px;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.groups-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
  max-height: none;
}

.group-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.group-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.group-item:last-child,
.group-item:has(+ .widget-footer) {
  border-bottom: none;
}

.group-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.group-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.group-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.group-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.group-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.group-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-messages {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
}

.joined-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  flex-shrink: 0;
  background-color: #DBEDDB;
  color: #1C3829;
}

.not-joined-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  flex-shrink: 0;
  background-color: #f3f4f6;
  color: rgba(0, 0, 0, 0.6);
}

.widget-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  flex-shrink: 0;
  padding-top: 4px;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.view-all-link:hover {
  color: #2563eb;
}

.no-groups {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}
</style>
